import { useState, useEffect } from 'react';
import axios from 'axios';
import { Scissors, Star, BookOpen, FileText, TrendingUp } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    services: 0,
    reviews: 0,
    blogPosts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [servicesRes, reviewsRes, blogRes] = await Promise.all([
          axios.get(`${API}/services?active_only=false`),
          axios.get(`${API}/reviews?active_only=false`),
          axios.get(`${API}/blog?published_only=false&limit=100`)
        ]);
        
        setStats({
          services: servicesRes.data.length,
          reviews: reviewsRes.data.length,
          blogPosts: blogRes.data.length
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { title: 'Services', value: stats.services, icon: Scissors, color: 'bg-emerald-100 text-emerald-600' },
    { title: 'Avis clients', value: stats.reviews, icon: Star, color: 'bg-amber-100 text-amber-600' },
    { title: 'Articles blog', value: stats.blogPosts, icon: BookOpen, color: 'bg-blue-100 text-blue-600' },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {statCards.map((stat, index) => (
            <Card key={index} className="border-stone-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-stone-500 text-sm mb-1">{stat.title}</p>
                    <p className="font-heading font-bold text-3xl text-stone-900">
                      {loading ? '-' : stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Actions rapides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <a 
                href="/admin/services" 
                className="p-4 rounded-xl border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-center"
                data-testid="quick-action-services"
              >
                <Scissors className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
                <p className="font-medium text-stone-900">Gérer les services</p>
              </a>
              <a 
                href="/admin/reviews" 
                className="p-4 rounded-xl border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-center"
                data-testid="quick-action-reviews"
              >
                <Star className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                <p className="font-medium text-stone-900">Gérer les avis</p>
              </a>
              <a 
                href="/admin/blog" 
                className="p-4 rounded-xl border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-center"
                data-testid="quick-action-blog"
              >
                <BookOpen className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="font-medium text-stone-900">Gérer le blog</p>
              </a>
              <a 
                href="/admin/pages" 
                className="p-4 rounded-xl border border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-center"
                data-testid="quick-action-pages"
              >
                <FileText className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                <p className="font-medium text-stone-900">Configurer les pages</p>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-stone-900 mb-1">
                  Bienvenue dans votre back-office !
                </h3>
                <p className="text-stone-600 text-sm">
                  Gérez facilement vos services, avis clients, articles de blog et les zones de réservation 
                  des pages Abonnement et Intervention. Utilisez le menu de gauche pour naviguer.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
